import { FC, useState } from 'react';
import { useHistory } from 'react-router';
import { Spacer, Text } from '@chakra-ui/react';
import { format } from 'date-fns';

import { isCreditCardPendingOrder } from 'common/creditCards/isCreditCardPendingOrder';
import Button from 'components/_core/Button';
import DeactivationDatePicker from 'features/PaymentCards/components/DeactivationDatePicker';
import NavigationHeader from 'features/PaymentCards/components/NavigationHeader';

import { CreditCard } from '../../../../../hooks/useCreditCards';
import SidebarFooter from '../components/SidebarFooter';
import DeactivationDateInfo from './DeactivationDateInfo';
import { useUpdateCreditCardDeactivationDate } from './hooks';
import locales from './locales';

interface DeactivationDateProps {
  creditCard: CreditCard;
}

const DeactivationDate: FC<DeactivationDateProps> = ({ creditCard }) => {
  const isTemporaryCard = !isCreditCardPendingOrder(creditCard)
    ? !!creditCard.deactivateAt
    : false;

  const [chosenDate, setChosenDate] = useState<Date | null | undefined>(
    undefined,
  );
  const [currentView, setCurrentView] = useState<'date-picker' | 'info'>(
    isTemporaryCard ? 'date-picker' : 'info',
  );
  const history = useHistory();

  const hasChosenDate = chosenDate !== undefined;

  const isDatePickerView = currentView === 'date-picker';

  const { loading, updateCardDeactivationDate } =
    useUpdateCreditCardDeactivationDate();

  const { creditCardId } = creditCard;

  // User can directly confirm on date picker if the card is temporary instead of info screen
  const shouldConfirmDate = isTemporaryCard || !isDatePickerView;

  const handleDateMutation = () => {
    if (chosenDate) {
      const dateForQuery = format(chosenDate, 'yyyy-MM-dd');
      updateCardDeactivationDate(creditCardId, dateForQuery);
    }
  };

  const onConfirm = () => {
    if (shouldConfirmDate) {
      handleDateMutation();
    } else {
      setCurrentView('info');
    }
  };

  const onActionClicked = () => {
    setCurrentView('date-picker');
  };

  if (isCreditCardPendingOrder(creditCard)) {
    return (
      <>
        <NavigationHeader backTo={`/cards/${creditCardId}`} />
        <Text>{locales.cardPendingCreation}</Text>
        <Spacer />
        <SidebarFooter>
          <Button
            onClick={() => {
              history.goBack();
            }}
            variant="inline-secondary"
          >
            {locales.cancelDatePicker}
          </Button>
        </SidebarFooter>
      </>
    );
  }

  return (
    <>
      <NavigationHeader backTo={`/cards/${creditCardId}`} />

      {isDatePickerView && !isCreditCardPendingOrder(creditCard) ? (
        <>
          <DeactivationDatePicker
            chosenDate={chosenDate}
            deactivateAt={creditCard.deactivateAt}
            maxDate={new Date(creditCard.expiryDate)}
            setChosenDate={setChosenDate}
          />
        </>
      ) : (
        <DeactivationDateInfo
          chosenDate={chosenDate}
          deactivateAt={creditCard.deactivateAt}
          onActionClicked={onActionClicked}
        />
      )}

      <Spacer />

      <SidebarFooter>
        <Button
          isDisabled={!hasChosenDate}
          isLoading={loading}
          onClick={onConfirm}
          width="100%"
        >
          {isDatePickerView ? locales.confirmDatePicker : locales.confirm}
        </Button>

        <Button
          onClick={() => {
            history.goBack();
          }}
          variant="inline-secondary"
        >
          {isDatePickerView ? locales.cancelDatePicker : locales.cancel}
        </Button>
      </SidebarFooter>
    </>
  );
};

export default DeactivationDate;
